import { ChannelLink, Chapter, Cut, ShowChannel } from './shows.model';

/**
 * How the Shows pages play a recording and move it to a chapter. Pure functions, so every rule
 * here is tested without a browser.
 *
 * ## A chapter names a chapter, not a time
 *
 * `?chapter=3` opens chapter 3 on whichever cut is playing, because a video with an intro starts
 * its chapters later than the audio does. The page looks up the chapter's time on the playing cut and reloads the player there:
 * no player SDK, at the cost of a second's reload (product owner, 2026-10-16).
 *
 * ## Which channels can seek
 *
 * Vimeo (`#t=`) and YouTube (`start=`) open at a time. Buzzsprout's embed is not known to, so an
 * audio cut on Buzzsprout shows its chapter times without moving the player; Spotify and Apple
 * Podcasts are not embedded at all, only linked.
 */

/** The names of the channels, which are brands and are not translated. */
export const CHANNEL_NAMES: Record<ShowChannel, string> = {
  vimeo: 'Vimeo',
  youtube: 'YouTube',
  buzzsprout: 'Buzzsprout',
  spotify: 'Spotify',
  apple_podcasts: 'Apple Podcasts',
};

const SEEKABLE: ShowChannel[] = ['vimeo', 'youtube'];

/** The link to embed: the one marked as the player, if it can be embedded. */
export function playerLink(links: ChannelLink[]): ChannelLink | null {
  const player = links.find((link) => link.isPlayer) ?? null;

  return player && embedUrl(player) !== null ? player : null;
}

/** The links that are not the player: "also on" buttons. */
export function otherLinks(links: ChannelLink[]): ChannelLink[] {
  const player = playerLink(links);

  return links.filter((link) => link !== player);
}

export function canSeek(link: ChannelLink | null): boolean {
  return link !== null && SEEKABLE.includes(link.channel);
}

/**
 * The address of the embedded player, opening at `startAt` seconds where the channel can, and
 * playing at once when asked — after a reader chose a chapter, not on arrival. `null` for a
 * channel that is not embedded.
 */
export function embedUrl(link: ChannelLink, startAt: number | null = null, autoplay = false): string | null {
  switch (link.channel) {
    case 'vimeo': {
      const id = link.externalId || lastNumber(link.url);

      if (!id) {
        return null;
      }

      const query = 'badge=0&autopause=0&dnt=1' + (autoplay ? '&autoplay=1' : '');

      return `https://player.vimeo.com/video/${encodeURIComponent(id)}?${query}` + (startAt ? `#t=${Math.floor(startAt)}s` : '');
    }
    case 'youtube': {
      const id = link.externalId || youTubeId(link.url);

      if (!id) {
        return null;
      }

      const query = 'rel=0' + (startAt ? `&start=${Math.floor(startAt)}` : '') + (autoplay ? '&autoplay=1' : '');

      return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${query}`;
    }
    case 'buzzsprout': {
      // Buzzsprout's iframe player is the episode's own page, asked for as an iframe.
      const match = link.url.match(/^https:\/\/www\.buzzsprout\.com\/\d+\/[^?#]+/);

      return match ? `${match[0]}?client_source=small_player&iframe=true` : null;
    }
    default:
      return null;
  }
}

/** Where a chapter starts on a cut, or `null` if it was not timed on that cut. */
export function chapterStart(chapter: Chapter, cutId: string | null): number | null {
  return chapter.times.find((time) => time.cut === cutId)?.startsAtSeconds ?? null;
}

/** The chapter a `?chapter=` value names, by position; `null` for anything else. */
export function chapterFromParam(value: string | null, chapters: Chapter[]): Chapter | null {
  if (value === null || !/^\d+$/.test(value)) {
    return null;
  }

  return chapters.find((chapter) => chapter.position === Number(value)) ?? null;
}

/**
 * The cut a reader starts on: a video in their language, else a video in the episode's language,
 * else any video, else the first cut — always one that can be played on our page, if there is
 * one.
 */
export function defaultCut(cuts: Cut[], readerLanguage: string | null, episodeLanguage: string | null): Cut | null {
  const playable = cuts.filter((cut) => playerLink(cut.channelLinks) !== null);
  const pool = playable.length ? playable : cuts;
  const videos = pool.filter((cut) => cut.medium === 'video');

  return (
    videos.find((cut) => cut.language === readerLanguage) ??
    videos.find((cut) => cut.language === episodeLanguage) ??
    videos[0] ??
    pool[0] ??
    null
  );
}

/**
 * The cut to play a chapter on: the one playing, if the chapter was timed on it and it can seek;
 * else another cut on which it can. `null` when no cut can open at the chapter.
 */
export function cutForChapter(chapter: Chapter, current: Cut | null, cuts: Cut[]): Cut | null {
  const seekableWithTime = (cut: Cut) => canSeek(playerLink(cut.channelLinks)) && chapterStart(chapter, cut.id) !== null;

  if (current && seekableWithTime(current)) {
    return current;
  }

  return cuts.find(seekableWithTime) ?? null;
}

/** `m:ss`, or `h:mm:ss` from an hour, as the chapter lists write it. */
export function formatTimecode(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, '0');

  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Black or white, whichever reads better on a show's colour. Nova checks only a colour's format,
 * so the contrast is decided here. WCAG relative luminance: white text on anything darker than
 * the midpoint.
 */
export function readableTextColour(background: string): '#000000' | '#ffffff' {
  const hex = background.replace('#', '');

  if (!/^[0-9a-f]{6}$/i.test(hex)) {
    return '#000000';
  }

  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;

    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };

  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);

  // Contrast with white is (1.05)/(L+0.05); with black (L+0.05)/0.05. Equal at L ≈ 0.179.
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

function lastNumber(url: string): string | null {
  return url.match(/(\d+)\/?(?:[?#].*)?$/)?.[1] ?? null;
}

function youTubeId(url: string): string | null {
  return url.match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([\w-]{6,})/)?.[1] ?? null;
}
